import { useMemo, useState } from "react";
import { createContainer } from "unstated-next";
import { useHistogramData } from "../../hooks/useHistogram";
import { getCategoryName, getTagName } from "../../constants/analyticsConstants/generativeCallReview";
import { ocean_blue } from "../../constants/colors";

export type HistogramParams = {
    applicationId: string;
    startDate: string;   // local naive, e.g. "2025-09-17 11:59 pm" or "YYYY-MM-DD HH:mm"
    endDate: string;     // local naive
    categories: string[];
    tags: string[];
    featureFlagId?: string;
};

type InitialState = Partial<HistogramParams>;

function useHistogramContainer(initialState: InitialState = {}) {
    // --- Params state the app can update from outside (mirrors your “container” style)
    const [params, setParams] = useState<HistogramParams>({
        applicationId: initialState.applicationId ?? "",
        startDate: initialState.startDate ?? "",
        endDate: initialState.endDate ?? "",
        categories: initialState.categories ?? [],
        tags: initialState.tags ?? [],
        featureFlagId: initialState.featureFlagId,
    });

    // --- Helpers
    const splitDateString = (dateTime: string, isUtc: boolean) => {
        const parts = isUtc ? dateTime.split("T") : dateTime.split(" ");
        const date = parts[0];
        const time = isUtc
            ? parts[1].split(":")[0] + ":" + parts[1].split(":")[1]
            : parts[1] + (parts.length > 2 ? parts[2] : "");
        const [y, m, d] = date.split("-");
        return [y, m, d, time] as const;
    };

    const MONTH_MAP = new Map<number, string>([
        [1, "Jan."], [2, "Feb."], [3, "Mar."], [4, "Apr."], [5, "May."], [6, "Jun."],
        [7, "Jul."], [8, "Aug."], [9, "Sep."], [10, "Oct."], [11, "Nov."], [12, "Dec."]
    ]);

    // --- Derived inputs
    const sdSplit = useMemo(() => (params.startDate ? splitDateString(params.startDate, false) : ["", "", "", ""] as const), [params.startDate]);
    const edSplit = useMemo(() => (params.endDate ? splitDateString(params.endDate, false) : ["", "", "", ""] as const), [params.endDate]);

    const interval = useMemo(() => {
        const sameDay = sdSplit[0] && edSplit[0] &&
            sdSplit[0] === edSplit[0] && sdSplit[1] === edSplit[1] && sdSplit[2] === edSplit[2];
        return sameDay ? 2 : 3;
    }, [sdSplit, edSplit]);

    const toIsoString = useMemo(() => (dateTimeStr: string): string => {
        if (!dateTimeStr) return "";
        const [date, time, meridiem] = dateTimeStr.split(" ");
        let [hours, minutes] = time.split(":");

        // Convert to 24-hour format
        let hour = parseInt(hours);
        if (meridiem?.toLowerCase() === "pm" && hour !== 12) hour += 12;
        if (meridiem?.toLowerCase() === "am" && hour === 12) hour = 0;

        // Pad with leading zeros
        const formattedHour = hour.toString().padStart(2, "0");
        return `${date}T${formattedHour}:${minutes}:00`;
    }, []);

    const startDateForApi = useMemo(() => toIsoString(params.startDate), [params.startDate]);
    const endDateForApi = useMemo(() => toIsoString(params.endDate), [params.endDate]);

    // --- Fetch (hook at top-level; no conditionals)
    const { data, loading, error } = useHistogramData({
        applicationId: params.applicationId,
        startDate: startDateForApi,
        endDate: endDateForApi,
        categories: params.categories,
        tags: params.tags,
        interval
    });

    const compareDates = (date1: string, date2: string): number => {
        if (!date1 || !date2) return 0;
        const time1 = new Date(date1).getTime();
        const time2 = new Date(date2).getTime();
        if (time1 > time2) return 1;
        if (time1 < time2) return -1;
        return 0;
    };


    // --- Derived presentation values
    const { title, subtitle, mappedData } = useMemo(() => {

        const mapDay = (day: string, month: string) => `${MONTH_MAP.get(Number(month))} ${day}`;

        const formattedStart = sdSplit[0] ? mapDay(sdSplit[2], sdSplit[1]) : "";
        const formattedEnd = edSplit[0] ? mapDay(edSplit[2], edSplit[1]) : "";

        const title = "CALL VOLUME";
        let subtitle =
            formattedStart && formattedEnd
                ? (formattedStart === formattedEnd ? formattedStart : `${formattedStart}-${formattedEnd}`)
                : "";

        if (params.categories?.length) {
            const cats = params.categories.map(c => getCategoryName(c, params.featureFlagId));
            subtitle += (subtitle ? " | " : "") + `Included Categories: ${cats.join(", ")}`;
        }
        if (params.tags?.length) {
            const tags = params.tags.map(t => getTagName(t, params.featureFlagId));
            subtitle += (subtitle ? " | " : "") + `Included Tags: ${tags.join(", ")}`;
        }


        const dateForAxis = (isoLocal: string) => {
            const time = isoLocal;

            // Get actual JS Date
            const dateObj = new Date(time);

            // Day-of-week map → first letter
            const dayLetterMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            const dayLetter = dayLetterMap[dateObj.getDay()]; // 0 = Sunday

            const [, m, d, hhmm] = splitDateString(time, true);

            if (interval >= 3) {
                // Example: 11/23 (M)
                return `${m}/${d} (${dayLetter})`;
            } else {
                let hour = Number(hhmm.split(":")[0]);
                const minute = hhmm.split(":")[1];
                const ampm = hour >= 12 ? "PM" : "AM";
                hour = hour % 12 || 12;

                // Example: 3:45 PM (T)
                return `${hour}:${minute} ${ampm} (${dayLetter})`;
            }
        };

        let cleanData = []
        if (data?.length > 0 && data !== undefined && data !== null) {
            cleanData = data.map(datum => {
                //If the datum date is less than the startdate or greater than the endDate on the props and the count is 0, don't return it
                if ((compareDates(datum.date, startDateForApi) < 0 || compareDates(datum.date, endDateForApi) > 0) && datum.count === 0) return null;
                return datum;
            }).filter(Boolean);
        }

        const mappedData =
            (cleanData ?? []).map((d: any) => ({
                color: ocean_blue,
                x: dateForAxis(d.date),
                y: d.count,
                fullDate: d.key,
                link: null,
            }));

        return { title, subtitle, mappedData };
    }, [data, interval, sdSplit, edSplit, params.categories, params.tags, params.featureFlagId]);

    return {
        params,
        setParams,            // (updater: (prev: HistogramParams) => HistogramParams) => void
        title,
        subtitle,
        mappedData,
        data,
        loading,
        error
    };
}

const HistogramContainer = createContainer(useHistogramContainer);
export default HistogramContainer;